"use client";

import { X } from "lucide-react";

/* Pre-checkout cross-sell modal.
 *
 * Brian QA: "when user buys something, popup buy this 2 also and get
 * something discount". Fires when the buyer hits Buy on a wall-art
 * detail page. Surfaces a second size from the same family and pairs
 * it with the live APEX10 code so they save 10% on both prints if
 * they accept.
 *
 * No invented numbers — APEX10 is a real Stripe Promotion Code minted
 * by /api/discount, so the savings are real. */

export type CrossSellSibling = {
  id: string;
  slug: string;
  label: string; // e.g. "Square (1×1)"
  printSize: string; // e.g. "12×12 in"
  priceCents: number;
};

export function CrossSellModal({
  open,
  primaryLabel,
  primaryPriceCents,
  bump,
  onAccept,
  onDecline,
  busy,
}: {
  open: boolean;
  primaryLabel: string;
  primaryPriceCents: number;
  bump: CrossSellSibling | null;
  onAccept: () => void;
  onDecline: () => void;
  busy: boolean;
}) {
  if (!open) return null;
  const total = primaryPriceCents + (bump?.priceCents ?? 0);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bundle this print"
      className="fixed inset-0 z-[80] grid place-items-center px-4"
      style={{
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        animation: "ax-modal-in 220ms ease-out both",
      }}
    >
      <div
        className="w-full max-w-[520px] overflow-hidden rounded-2xl border"
        style={{
          background: "rgba(10,10,12,0.98)",
          borderColor: "rgba(217,163,90,0.45)",
          boxShadow: "0 32px 80px -20px rgba(217,163,90,0.4)",
        }}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-4"
          style={{ borderColor: "rgba(255,255,255,0.06)" }}
        >
          <div
            className="font-mono text-[10px] uppercase inline-flex items-center gap-2"
            style={{
              color: "var(--color-accent)",
              letterSpacing: "0.16em",
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
            One more for the wall?
          </div>
          <button
            type="button"
            onClick={onDecline}
            aria-label="Skip the bundle"
            className="rounded-md p-1.5 text-white/65 hover:text-white"
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5">
          <h3
            className="text-white"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(22px, 3vw, 30px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Pair it with a {bump?.label ?? "second size"} —{" "}
            <span style={{ color: "var(--color-accent)" }}>save 10%</span>
          </h3>

          <p
            className="mt-3 text-[14px] leading-[1.5]"
            style={{ color: "rgba(255,255,255,0.78)" }}
          >
            Buyers who hang two sizes together fill the wall instead of leaving
            it empty. Use code{" "}
            <code
              className="rounded px-1.5 py-0.5 font-mono text-[12px]"
              style={{
                background: "rgba(217,163,90,0.18)",
                color: "var(--color-accent)",
              }}
            >
              APEX10
            </code>{" "}
            at checkout — saves 10% on the pair.
          </p>

          {bump && (
            <div
              className="mt-5 flex items-center justify-between rounded-xl border px-4 py-3"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <div>
                <div
                  className="font-mono text-[10px] uppercase"
                  style={{
                    color: "var(--color-accent)",
                    letterSpacing: "0.14em",
                  }}
                >
                  + Add this size
                </div>
                <div
                  className="mt-1 text-white"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 18,
                    lineHeight: 1.1,
                  }}
                >
                  {bump.label}
                </div>
                <div
                  className="mt-1 text-[12px]"
                  style={{ color: "rgba(255,255,255,0.6)" }}
                >
                  {bump.printSize}
                </div>
              </div>
              <span
                className="font-mono text-[18px] text-white"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                +${(bump.priceCents / 100).toFixed(0)}
              </span>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
            <button
              type="button"
              onClick={onAccept}
              disabled={busy || !bump}
              data-interactive
              className="flex-1 rounded-lg px-4 py-3 text-[14px] font-medium disabled:opacity-50"
              style={{
                background: "var(--color-accent)",
                color: "#000",
              }}
            >
              {busy
                ? "Opening checkout…"
                : `Add both — $${(total / 100).toFixed(0)} (use APEX10)`}
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={busy}
              data-interactive
              className="rounded-lg border px-4 py-3 text-[13px] text-white"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              Just this print
            </button>
          </div>

          <p
            className="mt-4 font-mono text-[10px] uppercase"
            style={{
              color: "rgba(255,255,255,0.45)",
              letterSpacing: "0.14em",
            }}
          >
            14-day guarantee · Instant download both files
          </p>
        </div>
      </div>

      <style>{`
        @keyframes ax-modal-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
